import { AxiosResponse } from 'axios';
import { observable, runInAction, makeAutoObservable } from 'mobx';
import { IButtonNavigationItem } from 'telia-front-react';
import ErrorUtil from '../../error/ErrorUtil';
import API from '../../util/ApiUtil';
import { FetchStatus } from '../../util/FetchStatus';
import LanguageStore from '../LanguageStore';
import { RootStore } from '../RootStore';

const THIRTY_SECONDS = 30 * 1000;

export const ALL_CATEGORIES_ID = '22';

export interface Image {
  poster?: string;
  small?: string;
  medium?: string;
  large?: string;
}

export interface Movie {
  id: number;
  name: string;
  type: string;
  originalName: string;
  images: Image;
}

export interface MovieCategoriesHolder {
  items?: IButtonNavigationItem[];
  status: string;
}

export interface MoviesHolder {
  items?: Movie[];
  totalCount?: number;
  status: string;
}

export interface MovieCategoriesParams {
  rootCategoryId: string;
  type: string;
  vodType: string;
  packetIds: string[];
  applicationId?: string;
  categoryId?: string;
}

export interface MoviesByCategoryParams {
  rootCategoryId: string;
  selectedCategoryId: string;
  type: string;
  vodType: string;
  packetIds: string[];
  page: number;
  rowsPerPage: number;
  offset: number;
  limit: number;
  moviesPerPage: number;
}

export interface MovieSearchResults {
  status: FetchStatus;
  results: Movie[];
}

export enum ContentType {
  SERIES = 'series',
  ASSETS = 'assets',
}

class MovieRentalCatalogStore {
  public searchResultsByTopCategoryAndCategory: Map<string, Map<string, MovieSearchResults>> = new Map();
  public categoriesMap: Map<string, MovieCategoriesHolder> = new Map();
  public moviesByCategoryAndPageMap: Map<string, MoviesHolder> = new Map();

  private previousSearch: string;
  private previousTopCategory: string;
  private languageStore: LanguageStore;

  constructor(rootStore: RootStore) {
    this.languageStore = rootStore.languageStore;
    makeAutoObservable(this);
  }

  public getMovies = (params: MoviesByCategoryParams): Movie[] => {
    return this.getMoviesListHolder(params).items.slice();
  };

  public getMoviesTotalCount = (params: MoviesByCategoryParams): number => {
    return this.getMoviesListHolder(params).totalCount;
  };

  public getCategories = (params: MovieCategoriesParams) => {
    return this.getCategoriesHolder(params)?.items;
  };

  public isFetchingCategories = (params: MovieCategoriesParams) => {
    return ['UNINITIALIZED', 'FETCHING'].includes(this.getCategoriesHolder(params).status);
  };

  public isFetchingMovies = (params: MoviesByCategoryParams) => {
    return ['UNINITIALIZED', 'FETCHING'].includes(this.getMoviesListHolder(params).status);
  };

  public isFetchingSearchResults = (topCategory: string) => {
    const resultsByCategory = this.searchResultsByTopCategoryAndCategory.get(topCategory);
    return resultsByCategory && !!Array.from(resultsByCategory.keys()).find((key) => resultsByCategory.get(key)?.status === FetchStatus.FETCHING);
  };

  public loadMovieCategories = async (params: MovieCategoriesParams) => {
    if (this.fetchCategoriesNotRequired(params)) {
      return;
    }
    runInAction(() => {
      this.categoriesMap.set(this.categoriesKey(params), {
        status: 'FETCHING',
      });
    });
    return this.fetchMovieCategories(params);
  };

  public loadMoviesByCategoryAndPage = async (params: MoviesByCategoryParams) => {
    if (this.fetchMoviesListNotRequired(params)) {
      return;
    }
    runInAction(() => {
      this.moviesByCategoryAndPageMap.set(this.moviesListKey(params), {
        status: 'FETCHING',
        items: [],
      });
    });
    return this.fetchMoviesByCategory(params);
  };

  public loadSearchResults = async (search: string, category: string, topCategory: string, allCategories: string[], packetIds?: string[]) => {
    if (!search) {
      return;
    }
    const searchResultsByCategory = this.searchResultsByTopCategoryAndCategory.get(topCategory);
    const searchResults = searchResultsByCategory?.get(`${category}`);

    if (!searchResults || this.searchInputChanged(search) || this.topCategoryChanged(topCategory)) {
      this.previousSearch = search;
      this.previousTopCategory = topCategory;
      await this.fetchSearchResults(search, `${category}`, topCategory, allCategories, packetIds);
    }
  };

  private fetchMovieCategories = async (params: MovieCategoriesParams) => {
    if (!params.rootCategoryId) {
      return;
    }
    const key = this.categoriesKey(params);
    const lang = this.languageStore.currentLanguage;
    const config = {
      params: {
        type: params.type,
        vodType: params.vodType,
        packetIds: params.packetIds,
        ui: 'tv-web',
        applicationId: params.applicationId,
      },
      timeout: THIRTY_SECONDS,
    };
    try {
      const response = await API.get(`/movie-rental/${lang}/category/${params.rootCategoryId}`, config);
      runInAction(() => {
        this.categoriesMap.set(key, {
          status: 'FETCHED',
          items: response.data.items,
        });
      });
    } catch (e) {
      ErrorUtil.pushErrorToTeliaRumAndLog(e as Error);
      runInAction(() => {
        this.categoriesMap.set(key, { status: 'NOT_FOUND', items: [] });
      });
    }
  };

  public fetchMoviesByCategory = async (params: MoviesByCategoryParams) => {
    const lang = this.languageStore.currentLanguage;
    const config = {
      params: {
        offset: params.offset,
        limit: params.limit,
        type: params.type,
        vodType: params.vodType,
        packetIds: params.packetIds,
        ui: 'tv-web',
      },
      timeout: THIRTY_SECONDS,
    };

    try {
      const response = await API.get(`/movie-rental/${lang}/category/${params.selectedCategoryId}/vod`, config);
      runInAction(() => {
        this.handleMoviesListFetchResponse(response, params);
      });
    } catch (e) {
      ErrorUtil.pushErrorToTeliaRumAndLog(e as Error);
      this.moviesByCategoryAndPageMap.set(this.moviesListKey(params), {
        status: 'NOT_FOUND',
        items: [],
      });
    }
  };

  public handleMoviesListFetchResponse(response: AxiosResponse<{ items: Movie[]; totalCount: number }>, params: MoviesByCategoryParams) {
    const previousItems = this.getMoviesListHolder(params).items || [];
    this.moviesByCategoryAndPageMap.set(this.moviesListKey(params), {
      status: 'FETCHED',
      items: previousItems.concat(response.data.items.slice(0, params.limit)),
      totalCount: response.data.totalCount,
    });
  }

  private fetchSearchResults = async (search: string, categoryId: string, types: string, allCategories: string[], packetIds?: string[]) => {
    const lang = this.languageStore.currentLanguage;
    const currentCategorySearchResults = this.getSearchResultsWrapper(categoryId, types);
    runInAction(() => {
      currentCategorySearchResults.status = FetchStatus.FETCHING;
    });
    for (categoryId of allCategories || []) {
      const searchResults = this.getSearchResultsWrapper(categoryId, types);
      try {
        runInAction(() => {
          searchResults.status = FetchStatus.FETCHING;
        });
        const items = (
          await API.get(`movie-rental/${lang}/vod/search`, {
            params: { query: search, categoryId, types, packetIds },
          })
        ).data.items;
        runInAction(() => {
          searchResults.results = items;
          searchResults.status = FetchStatus.FETCHED;
        });
      } catch (e) {
        ErrorUtil.pushErrorToTeliaRumAndLog(e as Error);
        runInAction(() => {
          searchResults.status = FetchStatus.ERROR;
          searchResults.results = [];
        });
      }
    }
    currentCategorySearchResults.status = FetchStatus.FETCHED;
  };

  private getSearchResultsWrapper(category: string, topCategory: string) {
    let searchResultsByCategory = this.searchResultsByTopCategoryAndCategory.get(topCategory);
    if (!searchResultsByCategory) {
      searchResultsByCategory = new Map();
      this.searchResultsByTopCategoryAndCategory.set(topCategory, searchResultsByCategory);
    }
    const searchResults = observable({
      status: FetchStatus.UNINITIALIZED,
      results: [] as Movie[],
    });
    searchResultsByCategory.set(category, searchResults);
    return searchResults;
  }

  public searchInputChanged = (search: string) => {
    return this.previousSearch !== search;
  };

  public topCategoryChanged = (topCategory: string) => {
    return this.previousTopCategory !== topCategory;
  };

  public clearSearchResults = () => {
    runInAction(() => {
      this.searchResultsByTopCategoryAndCategory.clear();
    });
  };

  private getCategoriesHolder = (params: MovieCategoriesParams): MovieCategoriesHolder => {
    if (!this.categoriesMap.get(this.categoriesKey(params))) {
      runInAction(() => {
        this.categoriesMap.set(this.categoriesKey(params), {
          status: 'UNINITIALIZED',
          items: [],
        });
      });
    }
    return this.categoriesMap.get(this.categoriesKey(params));
  };

  public getMoviesListHolder(params: MoviesByCategoryParams) {
    if (!this.moviesByCategoryAndPageMap.get(this.moviesListKey(params))) {
      this.moviesByCategoryAndPageMap.set(this.moviesListKey(params), {
        status: 'UNINITIALIZED',
        items: [],
        totalCount: 0,
      });
    }

    return this.moviesByCategoryAndPageMap.get(this.moviesListKey(params));
  }

  public moviesListKey(params: MoviesByCategoryParams) {
    const { rootCategoryId, selectedCategoryId, type, vodType, packetIds, page } = params;
    const lang = this.languageStore.currentLanguage;
    return `${lang}_${rootCategoryId}_${selectedCategoryId}_${type}_${vodType}_${page}_${(packetIds || []).join('_')}`;
  }

  private categoriesKey(params: MovieCategoriesParams) {
    const { rootCategoryId, type, vodType, packetIds } = params;
    const lang = this.languageStore.currentLanguage;
    return `${lang}_${rootCategoryId}_${type}_${vodType}_${(packetIds || []).join('_')}`;
  }

  private fetchCategoriesNotRequired(params: MovieCategoriesParams): boolean {
    const categoriesHolder = this.getCategoriesHolder(params);
    return categoriesHolder && !['UNINITIALIZED', 'NOT_FOUND'].includes(categoriesHolder.status);
  }

  private fetchMoviesListNotRequired(params: MoviesByCategoryParams): boolean {
    const moviesHolder = this.getMoviesListHolder(params);
    return moviesHolder?.items && !['UNINITIALIZED', 'NOT_FOUND'].includes(moviesHolder?.status);
  }
}

export default MovieRentalCatalogStore;
